import React, { useState, useEffect } from 'react';
import { Card, Table, Badge, Button, Row, Col, Form, Spinner, Alert, ProgressBar, Pagination } from 'react-bootstrap';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';
import adminService from '../../services/adminService';

interface SyncStatus {
  totalUsers: number;
  syncedUsers: number;
  failedUsers: number;
  lastSyncAt?: string;
  syncInProgress: boolean;
  autoSyncEnabled: boolean;
}

interface SyncLog {
  id: string;
  syncType: 'full' | 'incremental' | 'manual' | 'automatic';
  totalContacts: number;
  syncedCount: number;
  failedCount: number;
  startedAt: string;
  completedAt?: string;
  status: 'running' | 'completed' | 'failed';
  errorMessage?: string;
}

const Notifications: React.FC = () => {
  const [syncStatus, setSyncStatus] = useState<SyncStatus>({
    totalUsers: 0,
    syncedUsers: 0,
    failedUsers: 0,
    syncInProgress: false,
    autoSyncEnabled: false,
  });
  const [syncLogs, setSyncLogs] = useState<SyncLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalLogs, setTotalLogs] = useState(0);
  const [logsPerPage] = useState(20);

  const fetchSyncStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await adminService.getBrevoSyncStatus();
      
      if (response.success && response.data) {
        setSyncStatus({
          totalUsers: response.data.totalUsers || 0,
          syncedUsers: response.data.syncedUsers || 0,
          failedUsers: response.data.failedUsers || 0,
          lastSyncAt: response.data.lastSyncAt,
          syncInProgress: response.data.syncInProgress || false,
          autoSyncEnabled: response.data.autoSyncEnabled || false,
        });
      } else {
        // Show actual error instead of masking as zero state
        const errorMsg = response.error || response.message || 'Failed to fetch sync status';
        setError(errorMsg);
        toast.error(errorMsg);
        // Set zero state but keep error visible
        setSyncStatus({
          totalUsers: 0,
          syncedUsers: 0,
          failedUsers: 0,
          syncInProgress: false,
          autoSyncEnabled: false,
        });
      }
    } catch (err: any) {
      console.error('Sync status fetch error:', err);
      // Don't mask 404 errors - show them as actual errors
      const errorMsg = err.message || 'Failed to fetch sync status';
      setError(errorMsg);
      toast.error(errorMsg);
      // Set zero state but keep error visible
      setSyncStatus({
        totalUsers: 0,
        syncedUsers: 0,
        failedUsers: 0,
        syncInProgress: false,
        autoSyncEnabled: false,
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchSyncLogs = async (page: number = 1) => {
    try {
      const response = await adminService.getBrevoSyncLogs(logsPerPage, page);
      
      if (response.success && response.data) {
        // Handle new paginated response structure
        const normalizedLogs = response.data.map((log: any) => ({
          id: log.id,
          syncType: log.sync_type || log.syncType,
          totalContacts: log.total_contacts || log.totalContacts || 0,
          syncedCount: log.synced_count || log.syncedCount || 0,
          failedCount: log.failed_count || log.failedCount || 0,
          startedAt: log.started_at || log.startedAt,
          completedAt: log.completed_at || log.completedAt,
          status: log.status,
          errorMessage: log.error_message || log.errorMessage,
        }));
        
        setSyncLogs(normalizedLogs);
        setTotalLogs(response.pagination?.total || normalizedLogs.length || 0);
      } else {
        const errorMsg = response.error || response.message || 'Failed to fetch sync logs';
        setSyncLogs([]);
        setTotalLogs(0);
      }
    } catch (err: any) {
      // Don't mask errors - keep empty state
      setSyncLogs([]);
      setTotalLogs(0);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchSyncLogs(page);
  };

  useEffect(() => {
    fetchSyncStatus();
    fetchSyncLogs(currentPage);
  }, [currentPage]);

  // Separate effect for polling to avoid dependency issues
  useEffect(() => {
    if (syncStatus.syncInProgress) {
      const interval = setInterval(() => {
        fetchSyncStatus();
      }, 3000);

      // Set timeout to prevent infinite polling
      const timeout = setTimeout(() => {
        clearInterval(interval);
        setSyncStatus(prev => ({ ...prev, syncInProgress: false }));
        toast.error('Sync timed out after 3 minutes');
        setError('Sync operation timed out');
      }, 180000); // 3 minutes

      return () => {
        clearInterval(interval);
        clearTimeout(timeout);
      };
    }
  }, [syncStatus.syncInProgress]);

  const handleSyncAll = async () => {
    if (!window.confirm('This will sync all users from bookings to Brevo. This may take a few minutes. Continue?')) {
      return;
    }

    try {
      setSyncing(true);
      setError(null);
      
      const response = await adminService.syncAllUsersToBrevo();
      
      if (response.success) {
        toast.success(response.message || 'Full sync started. This may take a few minutes.');
        
        // Update status to show sync in progress
        setSyncStatus(prev => ({ ...prev, syncInProgress: true }));
        
        // Poll for completion with timeout
        let pollCount = 0;
        const maxPolls = 60; // 3 minutes with 3-second intervals
        
        const checkInterval = setInterval(async () => {
          pollCount++;
          
          try {
            await fetchSyncStatus();
            const currentStatus = await adminService.getBrevoSyncStatus();
            
            if (currentStatus.success && !currentStatus.data?.syncInProgress) {
              clearInterval(checkInterval);
              
              // Only show success message - no failure messages since all users are syncing
              toast.success('Sync completed successfully!');
              
              fetchSyncLogs();
            } else if (pollCount >= maxPolls) {
              clearInterval(checkInterval);
              setSyncStatus(prev => ({ ...prev, syncInProgress: false }));
              toast.error('Sync timed out after 3 minutes');
              setError('Sync operation timed out');
            }
          } catch (err) {
            console.error('Polling error:', err);
            if (pollCount >= maxPolls) {
              clearInterval(checkInterval);
              setSyncStatus(prev => ({ ...prev, syncInProgress: false }));
              toast.error('Sync monitoring failed');
            }
          }
        }, 3000);
      } else {
        const errorMsg = response.error || 'Failed to start sync';
        toast.error(errorMsg);
        setError(errorMsg);
      }
    } catch (err: any) {
      console.error('Sync all error:', err);
      const errorMsg = err.message || 'Failed to start sync';
      toast.error(errorMsg);
      setError(errorMsg);
      setSyncStatus(prev => ({ ...prev, syncInProgress: false }));
    } finally {
      setSyncing(false);
    }
  };

  const handleIncrementalSync = async () => {
    try {
      setSyncing(true);
      setError(null);
      
      const response = await adminService.syncIncrementalUsersToBrevo();
      
      if (response.success) {
        toast.success(response.message || 'Incremental sync started');
        
        setSyncStatus(prev => ({ ...prev, syncInProgress: true }));
        
        // Poll for completion with timeout
        let pollCount = 0;
        const maxPolls = 20; // 1 minute with 3-second intervals
        
        const checkInterval = setInterval(async () => {
          pollCount++;
          
          try {
            await fetchSyncStatus();
            const currentStatus = await adminService.getBrevoSyncStatus();
            
            if (currentStatus.success && !currentStatus.data?.syncInProgress) {
              clearInterval(checkInterval);
              
              // Only show success message - no failure messages since all users are syncing
              toast.success('Incremental sync completed successfully!');
              
              await fetchSyncLogs();
            } else if (pollCount >= maxPolls) {
              clearInterval(checkInterval);
              setSyncStatus(prev => ({ ...prev, syncInProgress: false }));
              toast.error('Incremental sync timed out');
              setError('Incremental sync operation timed out');
            }
          } catch (err) {
            console.error('Incremental sync polling error:', err);
            if (pollCount >= maxPolls) {
              clearInterval(checkInterval);
              setSyncStatus(prev => ({ ...prev, syncInProgress: false }));
              toast.error('Incremental sync monitoring failed');
            }
          }
        }, 3000);
      } else {
        const errorMsg = response.error || 'Failed to start incremental sync';
        toast.error(errorMsg);
        setError(errorMsg);
      }
    } catch (err: any) {
      console.error('Incremental sync error:', err);
      const errorMsg = err.message || 'Failed to start incremental sync';
      toast.error(errorMsg);
      setError(errorMsg);
      setSyncStatus(prev => ({ ...prev, syncInProgress: false }));
    } finally {
      setSyncing(false);
    }
  };

  const getSyncTypeBadge = (type: string) => {
    const variants: Record<string, { bg: string; label: string }> = {
      'full': { bg: 'primary', label: 'Full Sync' },
      'incremental': { bg: 'info', label: 'Incremental' },
      'manual': { bg: 'warning', label: 'Manual' },
      'automatic': { bg: 'success', label: 'Automatic' },
    };
    const config = variants[type] || { bg: 'secondary', label: type };
    return <Badge bg={config.bg}>{config.label}</Badge>;
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, string> = {
      'completed': 'success',
      'running': 'warning',
      'failed': 'danger',
    };
    return <Badge bg={variants[status] || 'secondary'}>{status}</Badge>;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const syncProgress = syncStatus.totalUsers > 0 
    ? (syncStatus.syncedUsers / syncStatus.totalUsers) * 100 
    : 0;

  return (
    <>
      <Helmet>
        <title>Brevo Sync - MyCinemas Admin Panel</title>
      </Helmet>

      <div className="fade-in">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h2 className="mb-1">Brevo Contact Sync</h2>
            <p className="text-muted mb-0">Sync user data to Brevo for marketing campaigns</p>
            <small className="text-muted">
              <i className="bi-info-circle me-1"></i>
              Create and manage campaigns in{' '}
              <a href="https://app.brevo.com" target="_blank" rel="noopener noreferrer">
                Brevo Dashboard
              </a>
            </small>
          </div>
          <div className="d-flex gap-2">
            <Button 
              variant="outline-primary" 
              onClick={handleIncrementalSync}
              disabled={syncing || syncStatus.syncInProgress}
            >
              {syncing ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Syncing...
                </>
              ) : (
                <>
                  <i className="bi-arrow-repeat me-1"></i>
                  Incremental Sync
                </>
              )}
            </Button>
            <Button 
              variant="primary" 
              onClick={handleSyncAll}
              disabled={syncing || syncStatus.syncInProgress}
            >
              {syncing ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Syncing...
                </>
              ) : (
                <>
                  <i className="bi-cloud-upload me-1"></i>
                  Sync All Users
                </>
              )}
            </Button>
          </div>
        </div>

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Sync Status Cards */}
        <Row className="mb-4">
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <i className="bi-people fs-1 text-primary mb-2"></i>
                <h4>{syncStatus.totalUsers.toLocaleString()}</h4>
                <small className="text-muted">Total Users</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <i className="bi-check-circle fs-1 text-success mb-2"></i>
                <h4>{syncStatus.syncedUsers.toLocaleString()}</h4>
                <small className="text-muted">Synced to Brevo</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <i className="bi-x-circle fs-1 text-danger mb-2"></i>
                <h4>{syncStatus.failedUsers}</h4>
                <small className="text-muted">Failed</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <i className="bi-clock-history fs-1 text-info mb-2"></i>
                <h6 className="mb-1">
                  {syncStatus.lastSyncAt 
                    ? formatDate(syncStatus.lastSyncAt)
                    : 'Never'
                  }
                </h6>
                <small className="text-muted">Last Sync</small>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Sync Progress */}
        {syncStatus.syncInProgress && (
          <Card className="mb-4 border-primary">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="mb-0">
                  <Spinner size="sm" className="me-2" />
                  Sync in Progress...
                </h6>
                <Badge bg="primary">
                  {syncStatus.syncedUsers} / {syncStatus.totalUsers}
                </Badge>
              </div>
              <ProgressBar 
                now={syncProgress} 
                label={`${Math.round(syncProgress)}%`}
                animated
              />
            </Card.Body>
          </Card>
        )}

        {/* Info Card */}
        <Alert variant="info" className="mb-4">
          <h6>
            <i className="bi-lightbulb me-2"></i>
            How it works
          </h6>
          <ul className="mb-0">
            <li>User data is extracted from <strong>bookings</strong> and synced to Brevo as contacts</li>
            <li>Each unique user (by email/mobile) from bookings becomes a Brevo contact</li>
            <li>Contact attributes include: total bookings, total spent, last booking date, favorite genres, etc.</li>
            <li>Create segments and campaigns in{' '}
              <a href="https://app.brevo.com" target="_blank" rel="noopener noreferrer">
                Brevo Dashboard
              </a>
            </li>
            <li>Automatic sync runs daily to keep data up-to-date</li>
            <li>Manual syncs are available for immediate updates</li>
          </ul>
        </Alert>

        {/* Sync Logs */}
        <Card>
          <Card.Header>
            <h5 className="mb-0">Sync History</h5>
          </Card.Header>
          <Card.Body className="p-0">
            {loading ? (
              <div className="text-center p-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-2 text-muted">Loading sync logs...</p>
              </div>
            ) : syncLogs.length === 0 ? (
              <div className="text-center p-5">
                <i className="bi-inbox fs-1 text-muted mb-3"></i>
                <p className="text-muted">No sync logs found</p>
              </div>
            ) : (
              <Table responsive hover className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Total</th>
                    <th>Synced</th>
                    <th>Failed</th>
                    <th>Started At</th>
                    <th>Completed At</th>
                    <th>Duration</th>
                  </tr>
                </thead>
                <tbody>
                  {syncLogs.map((log) => {
                    const duration = log.completedAt && log.startedAt
                      ? Math.round((new Date(log.completedAt).getTime() - new Date(log.startedAt).getTime()) / 1000)
                      : null;
                    
                    return (
                      <tr key={log.id}>
                        <td>{getSyncTypeBadge(log.syncType)}</td>
                        <td>{getStatusBadge(log.status)}</td>
                        <td>{(log.totalContacts || 0).toLocaleString()}</td>
                        <td>
                          <Badge bg="success">{(log.syncedCount || 0).toLocaleString()}</Badge>
                        </td>
                        <td>
                          {(log.failedCount || 0) > 0 ? (
                            <Badge bg="danger">{log.failedCount}</Badge>
                          ) : (
                            <span className="text-muted">0</span>
                          )}
                        </td>
                        <td>{formatDate(log.startedAt)}</td>
                        <td>
                          {log.completedAt ? formatDate(log.completedAt) : '-'}
                        </td>
                        <td>
                          {duration ? `${duration}s` : '-'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            )}
          </Card.Body>
          {totalLogs > logsPerPage && (
            <Card.Footer className="d-flex justify-content-between align-items-center">
              <div className="text-muted">
                Showing {((currentPage - 1) * logsPerPage) + 1} to {Math.min(currentPage * logsPerPage, totalLogs)} of {totalLogs} records
              </div>
              <Pagination>
                <Pagination.Prev 
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(currentPage - 1)}
                />
                {Array.from({ length: Math.ceil(totalLogs / logsPerPage) }, (_, i) => i + 1).map(page => (
                  <Pagination.Item
                    key={page}
                    active={page === currentPage}
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </Pagination.Item>
                ))}
                <Pagination.Next 
                  disabled={currentPage === Math.ceil(totalLogs / logsPerPage)}
                  onClick={() => handlePageChange(currentPage + 1)}
                />
              </Pagination>
            </Card.Footer>
          )}
        </Card>
      </div>
    </>
  );
};

export default Notifications;
